import React, { useMemo, useState } from 'react';
import {
  internalWelcomeEmail,
  welcomeEmail,
  internalPasswordEmail,
  passwordEmail,
  supplierSetupFormEmail,
  supplierFormCompletedEmail,
  complianceReminderEmail,
  supplierActivationEmail,
  documentExpiringSoonEmail,
  complianceDocPublishedEmail,
  documentExpiredEmail,
  briefReminderEmail,
  complianceAlertOnBriefAddEmail,
  outstandingComplianceDigestEmail,
  briefUpdatedEmail,
  briefNotificationEmail,
  supplierResponseEmail,
  supplierAcceptedEmail,
  briefAwardedInternalEmail,
  awardWinEmail,
  awardDeclineEmail,
  briefClosedEmail,
  passwordResetEmail,
  sendEmail,
} from '../lib/email';
import { useCurrentInternalUser } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';

// Throwaway dev page: fires every email template in src/lib/email.js
// at a chosen recipient with plausible sample data. Built to help
// Robynne review every email Supply Pilot can send in bulk. Delete
// this file and its route in App.js once the review is done.
//
// Sends go through the existing /api/send-email Vercel function, so
// no new infra or env setup. Gated to internal users only because the
// route guard in App.js requires it; admins are the realistic audience
// either way.

const SAMPLE = {
  fullName: 'Robynne Elliott',
  supplierName: "Robynne's Donuts",
  partnerSupplierName: "Phil's Campervan Shop",
  briefName: 'Supercar Brunch Festival 2026',
  documentLabel: 'Public Liability Insurance',
  publishedDocLabel: 'Modern Slavery Statement',
  expiryFuture: '12 June 2026',
  expiryPast: '12 May 2026',
  deadline: '10 June 2026 at 17:00',
  password: 'Sample-Password-1234',
  totalPrice: 4250,
  awardedAmount: 4250,
  addedByName: 'Andy McNeil',
  awardMessage: 'Thanks for your time on this brief — looking forward to working together.',
  declineMessage: 'Thank you for your response — unfortunately on this occasion we went with another supplier.',
  closedMessage: 'Following discussion the project has been shelved.',
};

const buildPreviews = (recipient) => {
  const origin = (typeof window !== 'undefined' && window.location?.origin) || 'https://example.com';
  const portalUrl = `${origin}/portal`;
  const briefUrl  = `${origin}/briefs/sample-brief-id`;
  const supplierUrl = `${origin}/suppliers/sample-supplier-id`;
  const formUrl   = `${origin}/supplier-form/sample-token`;
  return [
    { key: 'internal_welcome', name: 'Internal welcome',
      build: () => internalWelcomeEmail({ fullName: SAMPLE.fullName, email: recipient, appUrl: origin }) },
    { key: 'supplier_welcome', name: 'Supplier welcome',
      build: () => welcomeEmail({ fullName: SAMPLE.fullName, email: recipient, appUrl: origin }) },
    { key: 'internal_password', name: 'Internal password',
      build: () => internalPasswordEmail({ fullName: SAMPLE.fullName, email: recipient, password: SAMPLE.password, appUrl: origin }) },
    { key: 'supplier_password', name: 'Supplier password',
      build: () => passwordEmail({ fullName: SAMPLE.fullName, email: recipient, password: SAMPLE.password, appUrl: origin }) },
    { key: 'supplier_setup_form', name: 'Supplier set-up form invite',
      build: () => supplierSetupFormEmail({ contactName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, formUrl }) },
    { key: 'supplier_form_completed', name: 'Supplier form completed (internal notification)',
      build: () => supplierFormCompletedEmail({ recipientName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, supplierUrl }) },
    { key: 'compliance_reminder', name: 'Compliance reminder (manual chase)',
      build: () => complianceReminderEmail({ contactName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, portalUrl }) },
    { key: 'supplier_activation', name: 'Supplier activation',
      build: () => supplierActivationEmail({ contactName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, portalUrl }) },
    { key: 'document_expiring_soon', name: 'Document expiring soon (cron)',
      build: () => documentExpiringSoonEmail({ contactName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, documentLabel: SAMPLE.documentLabel, expiryDate: SAMPLE.expiryFuture, portalUrl }) },
    { key: 'compliance_doc_published', name: 'New compliance document published (blast)',
      build: () => complianceDocPublishedEmail({ contactName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, documentLabel: SAMPLE.publishedDocLabel, portalUrl }) },
    { key: 'document_expired', name: 'Document expired (cron)',
      build: () => documentExpiredEmail({ contactName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, documentLabel: SAMPLE.documentLabel, expiryDate: SAMPLE.expiryPast, portalUrl }) },
    { key: 'brief_reminder', name: 'Brief reminder',
      build: () => briefReminderEmail({ contactName: SAMPLE.fullName, briefName: SAMPLE.briefName, deadline: SAMPLE.deadline, appUrl: origin }) },
    { key: 'compliance_alert_brief_add', name: 'Compliance alert on brief add (internal)',
      build: () => complianceAlertOnBriefAddEmail({
        recipientName: SAMPLE.fullName,
        supplierName: SAMPLE.partnerSupplierName,
        complianceStatus: 'amber',
        briefName: SAMPLE.briefName,
        projectNumber: 'FS1234',
        client: 'Renault',
        projectName: 'Supercar Brunch',
        addedByName: SAMPLE.addedByName,
        briefUrl,
        eventDate: '5 Jul 2026',
        documents: [
          { label: 'Public Liability Insurance', statusText: 'Expires 12 Jun · 14 days', statusColour: 'amber' },
          { label: 'Food Hygiene Certificate', statusText: 'Missing', statusColour: 'red' },
          { label: 'Method Statement', statusText: 'Green', statusColour: 'green' },
        ],
      }) },
    { key: 'outstanding_compliance_digest', name: 'Outstanding compliance digest (weekly, internal)',
      build: () => outstandingComplianceDigestEmail({
        recipientName: SAMPLE.fullName,
        items: [
          { supplierName: "Phil's Campervan Shop", documentLabel: 'Food Hygiene', statusText: 'Missing', statusColour: 'red' },
          { supplierName: 'Bloom & Co', documentLabel: 'PL Insurance', statusText: '8 days', statusColour: 'amber' },
          { supplierName: "Andy's BBQ Truck", documentLabel: 'Risk Assessment', statusText: 'Missing', statusColour: 'red' },
        ],
        overviewUrl: `${origin}/compliance`,
      }) },
    { key: 'brief_updated', name: 'Brief updated — amend notification (supplier)',
      build: () => briefUpdatedEmail({ contactName: SAMPLE.fullName, briefName: SAMPLE.briefName, deadline: SAMPLE.deadline, changeMessage: 'Updated the delivery date to 22 June and added rig requirements. Please re-review the brief and update your quote if needed.', briefUrl: `${origin}/briefs/sample-brief-id` }) },
    { key: 'brief_notification', name: 'New brief notification (supplier) — #17 LIGHT theme POC',
      build: () => briefNotificationEmail({ contactName: SAMPLE.fullName, briefName: SAMPLE.briefName, deadline: SAMPLE.deadline, appUrl: origin }) },
    { key: 'supplier_accepted', name: 'Supplier accepted brief (internal) — #17 LIGHT theme POC',
      build: () => supplierAcceptedEmail({ ownerName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, briefName: SAMPLE.briefName, projectNumber: 'FS9879', client: 'Kia', projectName: "The reason we're all here.", briefUrl }) },
    { key: 'supplier_response', name: 'Supplier response received (internal)',
      build: () => supplierResponseEmail({
        ownerName: SAMPLE.fullName,
        supplierName: SAMPLE.supplierName,
        briefName: SAMPLE.briefName,
        totalPrice: SAMPLE.totalPrice,
        briefUrl,
        coverage: '8 hours, setup & breakdown',
        attachmentCount: 2,
      }) },
    { key: 'brief_awarded_internal', name: 'Brief awarded — internal confirmation',
      build: () => briefAwardedInternalEmail({
        awarderName: SAMPLE.fullName,
        supplierName: SAMPLE.supplierName,
        briefName: SAMPLE.briefName,
        awardedAmount: SAMPLE.awardedAmount,
        eventDate: '5 Jul 2026',
        briefUrl,
      }) },
    { key: 'award_win', name: 'Brief awarded — supplier won',
      build: () => awardWinEmail({ contactName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, briefName: SAMPLE.briefName, awardedAmount: SAMPLE.awardedAmount, personalMessage: SAMPLE.awardMessage, briefUrl }) },
    { key: 'award_decline', name: 'Brief awarded — supplier not selected',
      build: () => awardDeclineEmail({ contactName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, briefName: SAMPLE.briefName, personalMessage: SAMPLE.declineMessage, briefUrl }) },
    { key: 'brief_closed', name: 'Brief closed without award',
      build: () => briefClosedEmail({ contactName: SAMPLE.fullName, supplierName: SAMPLE.supplierName, briefName: SAMPLE.briefName, personalMessage: SAMPLE.closedMessage, briefUrl }) },
    { key: 'password_reset', name: 'Password reset (Supabase template — tokens render as raw {{ }} text)',
      build: () => passwordResetEmail() },
  ];
};

export default function DevEmailPreviews() {
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [recipient, setRecipient] = useState('robynne.elliott@fusion-events.com');
  const [statuses, setStatuses] = useState({}); // key -> 'idle' | 'sending' | 'sent' | 'error'
  const [errors, setErrors] = useState({});    // key -> error message
  const [busy, setBusy] = useState(false);

  const previews = useMemo(() => buildPreviews(recipient), [recipient]);

  const sendOne = async (item) => {
    setStatuses(prev => ({ ...prev, [item.key]: 'sending' }));
    setErrors(prev => { const next = { ...prev }; delete next[item.key]; return next; });
    try {
      const { subject, html } = item.build();
      const tagged = `[Preview] ${subject}`;
      await sendEmail({ to: recipient, subject: tagged, html });
      setStatuses(prev => ({ ...prev, [item.key]: 'sent' }));
    } catch (err) {
      setStatuses(prev => ({ ...prev, [item.key]: 'error' }));
      setErrors(prev => ({ ...prev, [item.key]: err?.message || 'Unknown error' }));
    }
  };

  const sendAll = async () => {
    if (!recipient || busy) return;
    if (!window.confirm(`Send ${previews.length} preview emails to ${recipient}? They'll all arrive within ~30 seconds.`)) return;
    setBusy(true);
    for (const item of previews) {
      await sendOne(item);
    }
    setBusy(false);
  };

  const pillClass = (status) => {
    if (status === 'sending') return 'bg-[#E89A2E]/10 border-[#E89A2E]/40 text-[#7A4E10]';
    if (status === 'sent')    return 'bg-[#92D400]/10 border-[#92D400]/40 text-fusion-forest';
    if (status === 'error')   return 'bg-[#E0290A]/10 border-[#E0290A]/40 text-[#E0290A]';
    return 'bg-white border-line text-[#51534A]';
  };
  const pillLabel = (status) => {
    if (status === 'sending') return 'Sending…';
    if (status === 'sent')    return 'Sent';
    if (status === 'error')   return 'Failed';
    return 'Idle';
  };

  return (
    <div className="h-screen bg-canvas flex overflow-hidden">
      <Sidebar currentInternalUser={currentInternalUser} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="pt-8 px-8 mb-3">
          <p className="font-['Metropolis'] text-[#51534A] text-xs tracking-[2px] uppercase">/dev / email previews</p>
          <h1 className="fusion-h1 text-fusion-forest mt-1">Email previews</h1>
          <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-3 max-w-3xl leading-relaxed">
            Fires one preview of every email template in <span className="font-['Metropolis'] text-fusion-forest">src/lib/email.js</span> at the recipient below.
            Subjects are prefixed with <span className="font-['Metropolis'] text-fusion-forest">[Preview]</span> so they're easy to filter in your inbox.
            Delete this page when you're done reviewing.
          </p>
        </div>
        <div className="flex-1 overflow-auto px-8 pb-12">
          <div className="bg-white border border-line rounded-xl p-5 mb-4">
            <label className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A]">Recipient</label>
            <div className="flex items-center gap-3 mt-2">
              <input
                type="email"
                value={recipient}
                onChange={e => setRecipient(e.target.value)}
                className="flex-1 bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors" />
              <button
                type="button"
                onClick={sendAll}
                disabled={busy || !recipient}
                className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-bold px-5 py-2.5 hover:bg-[#a4e810] transition-colors disabled:opacity-50 whitespace-nowrap">
                {busy ? `Sending… (${Object.values(statuses).filter(s => s === 'sent').length}/${previews.length})` : `Send all ${previews.length} previews`}
              </button>
            </div>
          </div>
          <div className="bg-white border border-line rounded-xl overflow-hidden">
            {previews.map((item, i) => {
              const status = statuses[item.key] || 'idle';
              const error = errors[item.key];
              return (
                <div key={item.key} className={`flex items-center gap-4 px-5 py-3 ${i > 0 ? 'border-t border-line' : ''}`}>
                  <span className="font-['Metropolis'] text-[10px] text-[#51534A] w-6">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-['Metropolis'] text-sm font-semibold text-ink truncate">{item.name}</p>
                    {error && <p className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-0.5 truncate">{error}</p>}
                  </div>
                  <span className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase rounded-full px-2.5 py-0.5 border ${pillClass(status)}`}>
                    {pillLabel(status)}
                  </span>
                  <button
                    type="button"
                    onClick={() => sendOne(item)}
                    disabled={status === 'sending' || busy || !recipient}
                    className="bg-white border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:border-active-green hover:text-active-green transition-colors disabled:opacity-50 whitespace-nowrap">
                    Send
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
